import { Injectable } from '@angular/core';
import { FormControl, FormGroup, AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  AppointmentFormData, 
  AbstinenceFormData, 
  VacationFormData,
  FormValidation 
} from '../interfaces/appointment.interface';
import { FORM_CONFIG, ERROR_MESSAGES } from '../constants/appointment.constants';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  /**
   * Valida um formulário de appointment
   */
  validateAppointmentForm(form: FormGroup): FormValidation[] {
    const validations: FormValidation[] = [];
    
    // Validar campos obrigatórios
    if (!form.get('personId')?.value) {
      validations.push({
        field: 'personId',
        isValid: false,
        message: 'Pessoa é obrigatória',
        type: 'error'
      });
    }

    if (!form.get('activityId')?.value) {
      validations.push({
        field: 'activityId',
        isValid: false,
        message: 'Atividade é obrigatória',
        type: 'error'
      });
    }

    if (!form.get('description')?.value?.trim()) {
      validations.push({
        field: 'description',
        isValid: false,
        message: 'Descrição é obrigatória',
        type: 'error'
      });
    }

    // Validar data
    const createdAt = form.get('createdAt')?.value;
    if (createdAt && new Date(createdAt) > new Date()) {
      validations.push({
        field: 'createdAt',
        isValid: false,
        message: 'Não é possível criar avaliação para data futura',
        type: 'error'
      });
    }

    return validations;
  }

  /**
   * Valida um formulário de abstinência
   */
  validateAbstinenceForm(form: FormGroup): FormValidation[] {
    const validations: FormValidation[] = [];
    
    if (!form.get('description')?.value?.trim()) {
      validations.push({
        field: 'description',
        isValid: false,
        message: 'Descrição é obrigatória',
        type: 'error'
      });
    }

    const startDate = form.get('startDate')?.value;
    const endDate = form.get('endDate')?.value;

    if (!startDate) {
      validations.push({
        field: 'startDate',
        isValid: false,
        message: 'Data de início é obrigatória',
        type: 'error'
      });
    }

    if (!endDate) {
      validations.push({
        field: 'endDate',
        isValid: false,
        message: 'Data de fim é obrigatória',
        type: 'error'
      });
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      validations.push({
        field: 'endDate',
        isValid: false,
        message: 'Data de fim deve ser posterior à data de início',
        type: 'error'
      });
    }

    return validations;
  }

  /**
   * Valida um formulário de férias
   */
  validateVacationForm(form: FormGroup): FormValidation[] {
    const validations: FormValidation[] = [];
    
    const startDate = form.get('startDate')?.value;
    const endDate = form.get('endDate')?.value;

    if (!startDate) {
      validations.push({
        field: 'startDate',
        isValid: false,
        message: 'Data de início é obrigatória',
        type: 'error'
      });
    }

    if (!endDate) {
      validations.push({
        field: 'endDate',
        isValid: false,
        message: 'Data de fim é obrigatória',
        type: 'error'
      });
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const today = new Date();
      
      if (start < today) {
        validations.push({
          field: 'startDate',
          isValid: false,
          message: 'Data de início não pode ser no passado',
          type: 'error'
        });
      }

      if (end < start) {
        validations.push({
          field: 'endDate',
          isValid: false,
          message: 'Data de fim deve ser posterior à data de início',
          type: 'error'
        });
      }

      // Validar se não há sobreposição com outras férias
      const daysDifference = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDifference < 1) {
        validations.push({
          field: 'endDate',
          isValid: false,
          message: 'Período de férias deve ser de pelo menos 1 dia',
          type: 'error'
        });
      }
    }

    return validations;
  }

  /**
   * Validação assíncrona para verificar se uma data está disponível
   */
  validateDateAvailability = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null);
    }

    const selectedDate = new Date(control.value);
    const today = new Date();
    
    if (selectedDate > today) {
      return of({ futureDate: true });
    }

    return of(null);
  };

  /**
   * Validação assíncrona para verificar se não há conflito de horários
   */
  validateTimeConflict = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null);
    }

    // Aqui você pode implementar uma verificação real contra a API
    // Por enquanto, retorna null (sem erro)
    return of(null);
  };

  /**
   * Valida se um campo é obrigatório
   */
  requiredValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return { required: true };
    }
    return null;
  };

  /**
   * Valida se um campo tem o comprimento mínimo
   */
  minLengthValidator = (minLength: number) => {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.length < minLength) {
        return { minlength: { requiredLength: minLength, actualLength: value.length } };
      }
      return null;
    };
  };

  /**
   * Valida se um campo tem o comprimento máximo
   */
  maxLengthValidator = (maxLength: number) => {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.length > maxLength) {
        return { maxlength: { requiredLength: maxLength, actualLength: value.length } };
      }
      return null;
    };
  };

  /**
   * Valida se uma data é válida
   */
  dateValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return { invalidDate: true };
    }

    return null;
  };

  /**
   * Valida se uma data não é futura
   */
  pastDateValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const date = new Date(value);
    const today = new Date();
    
    if (date > today) {
      return { futureDate: true };
    }

    return null;
  };

  /**
   * Valida se uma data não é muito antiga (mais de 1 ano)
   */
  recentDateValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const date = new Date(value);
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    if (date < oneYearAgo) {
      return { tooOld: true };
    }

    return null;
  };

  /**
   * Obtém mensagem de erro para um campo
   */
  getErrorMessage(field: string, errors: ValidationErrors): string {
    if (errors['required']) {
      return `${field} é obrigatório`;
    }
    if (errors['minlength']) {
      return `${field} deve ter pelo menos ${errors['minlength'].requiredLength} caracteres`;
    }
    if (errors['maxlength']) {
      return `${field} deve ter no máximo ${errors['maxlength'].requiredLength} caracteres`;
    }
    if (errors['invalidDate']) {
      return `${field} deve ser uma data válida`;
    }
    if (errors['futureDate']) {
      return `${field} não pode ser no futuro`;
    }
    if (errors['tooOld']) {
      return `${field} não pode ser muito antiga`;
    }
    
    return `${field} é inválido`;
  }

  /**
   * Verifica se um formulário é válido
   */
  isFormValid(form: FormGroup): boolean {
    return form.valid && Object.keys(form.controls).every(key => {
      const control = form.get(key);
      return control ? control.valid : true;
    });
  }

  /**
   * Marca todos os campos de um formulário como tocados
   */
  markFormGroupTouched(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  /**
   * Limpa todos os erros de um formulário
   */
  clearFormErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control) {
        control.setErrors(null);
      }
    });
  }
}
